import React, { useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faChevronLeft, faChevronRight, faClock, faDownload, faEuroSign, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useStaffLoV } from '@/features/services/hooks/useStaffLoV';
import { useServicesLoV } from '@/features/services/hooks/useServicesLoV';
import { useUrlSync } from '@/hooks/useUrlSync';

const AppDashboard: React.FC = () => {
  // URL synchronizace pro vybraný den
  const [urlState, setUrlState] = useUrlSync('dashboard', {
    defaults: {
      selectedDate: dayjs().format('YYYY-MM-DD'),
    },
    keyMapping: {
      selectedDate: 'date',
    },
  });

  const { staff: staffList, isLoading: isLoadingStaff } = useStaffLoV(true);
  const { services: servicesList, isLoading: isLoadingServices } = useServicesLoV(true);
  const [selectedStaff, setSelectedStaff] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('D. MMMM YYYY');
  };

  const handleDateChange = (direction: 'prev' | 'next') => {
    const currentDate = dayjs(urlState.selectedDate);
    const newDate = direction === 'prev' ? currentDate.subtract(1, 'day') : currentDate.add(1, 'day');

    setUrlState({ selectedDate: newDate.format('YYYY-MM-DD') });
  };

  const handleTodayClick = () => {
    setUrlState({ selectedDate: dayjs().format('YYYY-MM-DD') });
  };

  const todayStats = [
    {
      title: 'Dnešní rezervace',
      value: '24',
      icon: faCalendarCheck,
      color: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: 'Dnešní tržby',
      value: '3,450 Kč',
      icon: faEuroSign,
      color: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Nové zákazníci',
      value: '7',
      icon: faUsers,
      color: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
    {
      title: 'Čekací lista',
      value: '3',
      icon: faClock,
      color: 'bg-red-50',
      iconColor: 'text-red-600',
    },
  ];

  return (
    <>
      {/* Calendar Header */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Kalendář rezervací</h1>
          <div className="flex items-center space-x-3">
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all flex items-center">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Nová rezervace
            </button>
            <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-all flex items-center">
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button onClick={() => handleDateChange('prev')} className="p-2 hover:bg-gray-100 rounded-lg">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <h2 className="text-lg font-medium">{urlState.selectedDate ? formatDate(urlState.selectedDate) : 'Načítání...'}</h2>
              <button onClick={() => handleDateChange('next')} className="p-2 hover:bg-gray-100 rounded-lg">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            <button onClick={handleTodayClick} className="text-sm text-green-600 hover:text-green-800">
              Dnes
            </button>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button className="px-3 py-1 text-sm bg-white rounded shadow-sm">Den</button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900">Týden</button>
              <button className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900">Měsíc</button>
            </div>

            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedStaff}
              onChange={e => setSelectedStaff(e.target.value)}
              disabled={isLoadingStaff}
            >
              <option value="">Všichni zaměstnanci</option>
              {(staffList ?? []).map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedService}
              onChange={e => setSelectedService(e.target.value)}
              disabled={isLoadingServices}
            >
              <option value="">Všechny služby</option>
              {(servicesList ?? []).map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Today's Overview Cards */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {todayStats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:transform hover:-translate-y-1 transition-transform">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconColor}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Placeholder for calendar grid and other sections */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Kalendář</h3>
        <p className="text-gray-600">Kalendář s rezervacemi bude implementován zde...</p>
      </section>
    </>
  );
};

export default AppDashboard;
